import { useMemo, useState, useCallback } from 'react';

import {
  Box,
  Center,
  Select,
  SelectButton,
  SelectOption,
  SelectValue,
  VirtualizedSelectOptions,
} from '@frontend/ui-library';
import { capitalizeFirstLetter } from '@frontend/utils';
import CountryFlag from './CountryFlag';

export interface Country {
  __typename?: 'Country';
  alpha2code?: string | null;
  name?: string | null;
}

interface CountryDropdownProps {
  countries: (Country | null)[];
  value?: string;
  onValueChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
}

export default function CountryDropdown({
  countries,
  value = '',
  onValueChange,
  label = 'Country',
  placeholder,
  disabled = false,
  testId,
}: CountryDropdownProps) {
  const sortedCountries = useMemo(() => {
    const validCountries = countries.filter(
      (country): country is Country =>
        country !== null && !!country?.alpha2code && !!country?.name
    );

    return [...validCountries].sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }, [countries]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedCountry = useMemo(() => {
    return sortedCountries.find((country) => country?.alpha2code === value);
  }, [sortedCountries, value]);

  const selectedIndex = useMemo(() => {
    return sortedCountries.findIndex(
      (country) => country?.alpha2code === value
    );
  }, [sortedCountries, value]);

  const estimateItemSize = useCallback(
    (index: number) => {
      const country = sortedCountries[index];
      if (!country?.name) return 52;

      // Base height: 32px padding + 18px line height
      const baseHeight = 50;
      // Estimate characters per line (roughly 35-40 chars fit in the dropdown width)
      const charsPerLine = 35;
      const estimatedLines = Math.ceil(country.name.length / charsPerLine);

      // Add extra height for each additional line (18px line height)
      return baseHeight + (estimatedLines - 1) * 18;
    },
    [sortedCountries]
  );

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      onOpenChange={setIsOpen}
    >
      <SelectButton
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        data-test-id={testId}
      >
        <SelectValue
          customValue={
            <Center inline>
              {selectedCountry && (
                <CountryFlag
                  width={21.3}
                  countryCode={selectedCountry?.alpha2code || ''}
                />
              )}
              <Box as="div" ml="8">
                {capitalizeFirstLetter(selectedCountry?.name || '')}
              </Box>
            </Center>
          }
        />
      </SelectButton>
      {sortedCountries.length > 0 && (
        <VirtualizedSelectOptions
          isOpen={isOpen}
          itemCount={sortedCountries.length}
          estimateSize={estimateItemSize}
          overscan={8}
          selectedIndex={selectedIndex >= 0 ? selectedIndex : undefined}
          onValueChange={onValueChange}
          getItemValue={(index: number) => {
            const country = sortedCountries[index];
            return country?.alpha2code || '';
          }}
          renderItem={(index: number, isFocused) => {
            const country = sortedCountries[index];
            if (!country?.alpha2code || !country?.name) return null;

            return (
              <SelectOption
                key={country.alpha2code}
                value={country.alpha2code}
                data-test-id={`${testId}-option-${country.alpha2code.toLowerCase()}`}
                data-focused={isFocused}
                style={
                  isFocused
                    ? { backgroundColor: 'var(--u-color-royal-050)' }
                    : undefined
                }
              >
                <Center inline>
                  <CountryFlag width={21.3} countryCode={country.alpha2code} />
                  <Box as="div" ml="8">
                    {capitalizeFirstLetter(country.name)}
                  </Box>
                </Center>
              </SelectOption>
            );
          }}
        />
      )}
    </Select>
  );
}
